/**
 * Host-mode chooser (#150, FR-9.5/9.6; #91 FR-11.1) — "Who runs the game?" as two
 * selectable, self-describing cards instead of a terse radio list. Each card explains
 * what to expect from that mode so a first-time host can tell them apart.
 *
 * One goal = one control: each card wraps a real, visually-hidden radio
 * (`name="host"`, `data-testid="host-human"|"host-auto"`), so keyboard / assistive-tech
 * semantics and the existing test hooks (`.check()`, `toBeChecked()`) are preserved
 * while the card itself is the visible target. Purely presentational — the chosen
 * `HostMode` is threaded into the create call by the parent lobby.
 */

import type { HostMode } from '@jeopardy/shared';

interface HostModeOption {
  mode: HostMode;
  testId: string;
  icon: string;
  title: string;
  /**
   * Optional maturity pill shown beneath the title (#188) — e.g. "Preview" on the
   * still-evolving auto-host path, so a first-time host knows what to expect.
   * Presentational only; no interaction (FR-9.5 preserved).
   */
  badge?: string;
  description: string;
  bullets: string[];
}

const OPTIONS: readonly HostModeOption[] = [
  {
    mode: 'HUMAN',
    testId: 'host-human',
    icon: '🎙️',
    title: 'Human host',
    description:
      'You run the game — read clues aloud, open the buzzer, and judge answers. Scores apply automatically as you judge.',
    bullets: [
      'Best when someone wants to emcee',
      'You control pacing & the timer',
      'Players buzz in from their phones',
    ],
  },
  {
    mode: 'AUTO',
    testId: 'host-auto',
    icon: '🤖',
    title: 'Auto-host (voice)',
    badge: 'Preview',
    description:
      'No host needed. The game reads each clue aloud, opens the buzzer for everyone at once, and judges spoken answers automatically.',
    bullets: [
      'Best when everyone wants to play',
      'Fair buzzer — opens for all at the same moment',
      'Falls back to typed answers if needed',
    ],
  },
];

interface HostModeCardsProps {
  value: HostMode;
  onChange: (mode: HostMode) => void;
}

export function HostModeCards({
  value,
  onChange,
}: HostModeCardsProps): React.JSX.Element {
  return (
    <fieldset className="host-cards" data-testid="host-choice">
      <legend className="host-cards__legend">Who runs the game?</legend>
      <div className="host-cards__grid">
        {OPTIONS.map((opt) => {
          const selected = value === opt.mode;
          return (
            <label
              key={opt.mode}
              className={`host-card${selected ? ' host-card--selected' : ''}`}
              data-selected={selected}
            >
              <input
                type="radio"
                name="host"
                className="host-card__radio"
                data-testid={opt.testId}
                checked={selected}
                onChange={() => onChange(opt.mode)}
              />
              <span className="host-card__marker" aria-hidden="true" />
              <span className="host-card__icon" aria-hidden="true">
                {opt.icon}
              </span>
              <span className="host-card__title">{opt.title}</span>
              {opt.badge && (
                <span
                  className="host-card__preview-badge host-card__preview-badge--amber-soft"
                  data-testid="host-preview-badge"
                >
                  {opt.badge}
                </span>
              )}
              <span className="host-card__description">{opt.description}</span>
              <ul className="host-card__bullets">
                {opt.bullets.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
